import { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/ui/Button/Button';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import useModalClass from 'hooks/useModalClass';
import MaximizeModal from 'components/shared/MaximizeModal';
import ScreenshotSupport from 'components/shared/ScreenshotSupport';
import useDraggableModal from 'hooks/useDraggableModal';
import useRecaptcha from 'hooks/useRecaptcha';
import { createPortal } from 'react-dom';
import { getSearchData, loginAs } from '../services/config';
import { MainContext } from '../contexts/MainContext';
import { showSuccess } from 'utils/utils';

interface MoSpecialRequestModalProps {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
}

const DifferentUserModal: FC<MoSpecialRequestModalProps> = ({
  openModal,
  setOpenModal,
  ...props
}) => {
  const [t] = useTranslation();
  const classList = useModalClass(openModal);
  const [options, setOptions] = useState<any>({});
  const { user, setUser, setLoginAsUser, loginAsUser } = useContext(MainContext);
  const { handleRecapcha } = useRecaptcha()
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const modalOverlayRef = useRef<HTMLDivElement>(null);

  const allOptions = useRef<any>();

  const [newDepartmentId, setNewDepartmentId] = useState<number>(-1);
  const [newBranchId, setNewBranchId] = useState<number>(-1);
  const [newUserCode, setNewUserCode] = useState<number>(-1);

  useEffect(() => {
    (async () => {
      const response = await getSearchData();
      if (response) {
        console.log(response.searchRequest);
        allOptions.current = {
          departments: [
            {
              label: t('mo.all_departments'),
              value: -1,
              html: (
                <div className="flex-between">
                  <span>{t('mo.all_departments')}</span>
                </div>
              ),
            },
            ...response.searchRequest.departments
              .map(d => ({
                label: d.departmentDesc + ' | ' + d.departmentId,
                value: d.departmentId,
                html: (
                  <div className="flex-between">
                    <span>{d.departmentDesc}</span>
                    <span>{d.departmentId}</span>
                  </div>
                ),
              }))
              .sort((a, b) => a.value - b.value),
          ],
          branches: [
            {
              label: 'All related branches',
              data: {
                departmentId: -1,
              },
              value: -1,
              html: (
                <div className="flex-between">
                  <span>All related branches</span>
                </div>
              ),
            },
            ...response.searchRequest.branches
              .map(b => ({
                label: b.branchDesc + ' | ' + b.branchId,
                value: b.branchId,
                data: {
                  departmentId: b.departmentId,
                },
                html: (
                  <div className="flex-between">
                    <span>{b.branchDesc}</span>
                    <span>{b.branchId}</span>
                  </div>
                )
              }))
              .sort((a, b) => a.value - b.value),
          ],
          users: response.searchRequest.users.map(b => ({
            label: b.user_f_name + ' | ' + b.user_code,
            value: b.user_code,
            data: {
              departmentId: b.departmentId,
              branchId: b.branchId,
            },
            html: (
              <div className="flex-between">
                <span>{b.user_f_name}</span>
                <span>{b.user_code}</span>
              </div>
            )
          })).sort((a, b) => a.value - b.value),
        };

        const relatedBranches = allOptions.current.branches.filter((b: any) => b.data.departmentId === user?.departmentId || b.data.departmentId === -1 || user?.departmentId === -1);
        const relatedUsers = allOptions.current.users.filter(
          (u: any) =>
            (u.data.departmentId === user?.departmentId ||
              u.data.departmentId === -1 ||
              user?.departmentId === -1) &&
            (u.data.branchId === user?.branchId || user?.branchId === -1)
        );

        setOptions({
          ...allOptions.current,
          branches: relatedBranches,
          users: relatedUsers,
        });

        setNewDepartmentId(user?.departmentId || -1);
        setNewBranchId(user?.branchId || -1);
        setNewUserCode(user?.userCode || -1);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal]);

// eslint-disable-next-line react-hooks/exhaustive-deps

  const onSelectDepartment = (departmentId: number) => {
    setNewDepartmentId(departmentId);

    const relatedBranches =
      allOptions.current.branches.filter((b: any) => b.data.departmentId === departmentId || b.data.departmentId === -1 || departmentId === -1);

    const relatedUsers =
      allOptions.current.users.filter((u: any) => u.data.departmentId === departmentId || u.data.departmentId === -1 || departmentId === -1);

    setOptions({
      ...allOptions.current,
      branches: relatedBranches,
      users: relatedUsers,
    });

    setNewBranchId(-1);
    setNewUserCode(-1);
  };

  const onSelectBranch = (branchId: number) => {
    setNewBranchId(branchId);

    const relatedUsers = allOptions.current.users.filter(
      (u: any) =>
        (u.data.departmentId === newDepartmentId ||
          u.data.departmentId === -1 ||
          newDepartmentId === -1) &&
        (u.data.branchId === branchId || branchId === -1)
    );

    setOptions({
      ...allOptions.current,
      branches: options.branches,
      users: relatedUsers,
    });

    setNewUserCode(-1);
  };

  const onSelectUser = (userCode: number) => {
    setNewUserCode(userCode);
  };

  const onSubmit = async () => {
    const recaptchaToken = await handleRecapcha();

    if (!recaptchaToken) return;

    const params = {
      userCode: newUserCode,
      departmentId: newDepartmentId,
      branchId: newBranchId,
      recaptchaToken: recaptchaToken,
    };

    const response = await loginAs(params);

    if (response?.userInfo) {
      const newUser = {
        ...(loginAsUser || user),
        ...response.userInfo,
        loginAsDuration: response.loginAsDuration,
        loginAsTime: new Date().toISOString(),
      };

      setLoginAsUser(loginAsUser || user);
      setUser(newUser);

      showSuccess(t('mo.logged_as_user_successfully'));

      localStorage.setItem('loginAsUser', JSON.stringify(loginAsUser || user));
      localStorage.setItem('user', JSON.stringify(newUser));

    }

    setOpenModal(false);
  };

  return createPortal(
    <div
      className="modal-overlay modal-overlay-top mo-message-modal-overlay modal-maximizable"
      ref={modalOverlayRef}
      onClick={(e) => e.target === e.currentTarget && setOpenModal(false)}
    >
      <div ref={modalRef} className={classList + ' modal'} style={modalStyle}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">
            {t('mo.set_different_user')}{' '}
          </h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes as any} onClick={() => setOpenModal(false)} />
          </div>
        </div>

        <div className="modal-body text-xsm mo-message-modal-body" style={{ padding: '0 1px' }}>
          <div>
            <div className="mb-20"></div>
            <SelectCustom
              value={newDepartmentId || ''}
              onChange={onSelectDepartment}
              options={options.departments || []}
              showHtmlPrev={true}
              movablePlaceholder={t('mo.department')}
              appendToBody={true}
              disabled={(user?.user_group_id || 0) < 60}
            />
            <div className="mb-20"></div>

            <SelectCustom
              value={newBranchId || ''}
              onChange={onSelectBranch}
              options={options.branches || []}
              showHtmlPrev={true}
              movablePlaceholder={t('mo.branch')}
              menuPosition="fixed"
              appendToBody={true}
            />
            <div className="mb-20"></div>

            <SelectCustom
              value={newUserCode || ''}
              onChange={onSelectUser}
              options={options.users || []}
              showHtmlPrev={true}
              movablePlaceholder={t('mo.user')}
              menuPosition="fixed"
              appendToBody={true}
            />
          </div>
          <div className="mb-20"></div>
        </div>

        <div className="modal-footer">
          <Button
            title={t('save')}
            className="btn btn-primary ml-0 mr-a"
            onClick={onSubmit}
            disabled={newUserCode === -1}
          />
          <Button
            title={t('cancel')}
            className="btn btn-default"
            onClick={() => setOpenModal(false)}
          />
        </div>
      </div>
    </div>
    , document.body);
};

export default DifferentUserModal;
